import { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime as fmtDateTimeImpl } from "@/lib/date";
import DoxaSelect from "@/components/doxa/DoxaSelect";

// Shared client-side search box, consistent with the Students screen pattern.
export function SearchBar({ value, onChange, placeholder = "Buscar…", testid = "search" }) {
  return (
    <div className="doxa-card p-3 mb-4 flex items-center gap-2">
      <Search className="w-4 h-4 text-[#6B655E] ml-2 shrink-0" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 bg-transparent outline-none text-sm py-1"
        data-testid={testid}
      />
      {value && (
        <button onClick={() => onChange("")} className="text-[#6B655E] hover:text-[#F75A1B] pr-1" data-testid={`${testid}-clear`} aria-label="Limpar busca">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// Normalizes text for accent-insensitive search.
export function norm(s) {
  return (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

// Chained dropdowns: Turma (required) -> Módulo (optional) -> Aula (optional).
// value = { class_group_id, module_id, lesson_id }
export function RelationSelects({ classGroups, value, onChange, prefix = "rel" }) {
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);

  useEffect(() => {
    if (value.class_group_id) {
      api.get("/modules", { params: { class_group_id: value.class_group_id } }).then(({ data }) => setModules(data)).catch(() => setModules([]));
      api.get("/lessons", { params: { class_group_id: value.class_group_id, status_filter: "all" } }).then(({ data }) => setLessons(data)).catch(() => setLessons([]));
    } else {
      setModules([]); setLessons([]);
    }
  }, [value.class_group_id]);

  const lessonOptions = value.module_id ? lessons.filter((l) => l.module_id === value.module_id) : lessons;

  return (
    <div className="grid sm:grid-cols-3 gap-3">
      <div>
        <label className="block text-sm font-medium mb-1">Turma *</label>
        <DoxaSelect
          value={value.class_group_id}
          onValueChange={(classGroupId) => onChange({ class_group_id: classGroupId, module_id: "", lesson_id: "" })}
          options={[{ value: "", label: "Selecione…" }, ...classGroups.map((c) => ({ value: c.id, label: c.name }))]}
          className="w-full"
          testId={`${prefix}-class`}
          ariaLabel="Turma"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Módulo</label>
        <DoxaSelect
          value={value.module_id || ""}
          onValueChange={(moduleId) => onChange({ ...value, module_id: moduleId, lesson_id: "" })}
          options={[{ value: "", label: "— Nenhum —" }, ...modules.map((m) => ({ value: m.id, label: m.name }))]}
          disabled={!value.class_group_id}
          className="w-full"
          testId={`${prefix}-module`}
          ariaLabel="Módulo"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Aula</label>
        <DoxaSelect
          value={value.lesson_id || ""}
          onValueChange={(lessonId) => onChange({ ...value, lesson_id: lessonId })}
          options={[{ value: "", label: "— Nenhuma —" }, ...lessonOptions.map((l) => ({ value: l.id, label: l.title }))]}
          disabled={!value.class_group_id}
          className="w-full"
          testId={`${prefix}-lesson`}
          ariaLabel="Aula"
        />
      </div>
    </div>
  );
}

export function StatusFilter({ value, onChange, options, prefix = "status" }) {
  return (
    <div className="inline-flex rounded-xl border border-[#E0D9CD] bg-white overflow-hidden" data-testid={`${prefix}-filter`}>
      {options.map((o) => (
        <button key={o.value} onClick={() => onChange(o.value)}
          className={`px-3 py-2 text-sm ${value === o.value ? "bg-[#F75A1B] text-white" : "text-[#6B655E] hover:bg-[#F6EFE4]"}`}
          data-testid={`${prefix}-${o.value}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function isScheduled(startsAt) {
  if (!startsAt) return false;
  const d = new Date(startsAt);
  return !isNaN(d) && d.getTime() > Date.now();
}

export function fmtDateTime(s) {
  return fmtDateTimeImpl(s);
}
